import {
  markAppointmentFormsComplete,
  useAppointmentFormsMarkedComplete,
  useTelemedicineAccess,
  type HealthieAnswer
} from "@internal/telemedicine"
import { useQueryClient } from "@tanstack/react-query"
import { TRPCClientError } from "@trpc/client"
import { useIntakeForms, removeCompletedIntakeForm } from "./useIntakeForms"
import { usePhotoIdOnFile } from "./usePhotoIdOnFile"
import { useSubmitIntakeForm } from "./useSubmitIntakeForm"
import { useUploadIdDocuments } from "./useUploadIdDocuments"
import { usePhotoIdTracking } from "./usePhotoIdTracking"

function isNotFoundError(error: unknown): boolean {
  if (!(error instanceof TRPCClientError)) return false
  const data = (error as TRPCClientError<never>).data as { code?: string } | undefined
  return data?.code === "NOT_FOUND"
}

export function usePreVisitIntake(appointmentId: string) {
  const queryClient = useQueryClient()
  const access = useTelemedicineAccess()
  const hasAccess = access.data != null && access.data.eligible && access.data.entry.status !== "single_unqualified"
  const intakeForms = useIntakeForms(appointmentId, hasAccess)
  const photoIdOnFile = usePhotoIdOnFile({ enabled: hasAccess })
  const submission = useSubmitIntakeForm(appointmentId)
  const upload = useUploadIdDocuments()
  const { onImageAdded } = usePhotoIdTracking()
  const formsMarkedComplete = useAppointmentFormsMarkedComplete(appointmentId)
  const forms = intakeForms.data?.forms
  const form = forms?.[0]
  // React Query v4 treats a no-data retry as loading rather than refetching. isFetched distinguishes
  // that retry from the initial request while keeping the query as the source of truth.
  const isPhotoIdRetrying = photoIdOnFile.isFetching && photoIdOnFile.isFetched

  const submitAnswers = async (answers: HealthieAnswer[]) => {
    if (!form) throw new Error("No intake form is connected to this appointment.")
    await submission.mutateAsync({ formId: form.id, answers })
    if (forms.length === 1) markAppointmentFormsComplete(queryClient, appointmentId)
    removeCompletedIntakeForm(queryClient, appointmentId, form.id)
  }

  const retryPhotoIdStatus = (): void => {
    void photoIdOnFile.refetch()
  }

  return {
    access: { isLoading: access.isLoading, isDenied: access.isError || !hasAccess },
    questionnaire: {
      form,
      isLoading: intakeForms.isLoading,
      error: intakeForms.error,
      isNotFound: isNotFoundError(intakeForms.error),
      hasForms: intakeForms.data?.hasForms,
      isComplete: formsMarkedComplete || (forms != null && forms.length === 0),
      isReady: !intakeForms.isLoading && !intakeForms.error && forms != null && forms.length > 0,
      program: intakeForms.data?.program ?? null,
      submitAnswers,
      hasSubmissionError: submission.isError
    },
    photoId: {
      isLoading: photoIdOnFile.isLoading && !isPhotoIdRetrying,
      hasStatusError: photoIdOnFile.isError || isPhotoIdRetrying,
      isRetrying: isPhotoIdRetrying,
      isRequired: !photoIdOnFile.isError && photoIdOnFile.data?.onFile !== true && !upload.isSuccess,
      retryStatus: retryPhotoIdStatus,
      isUploading: upload.isPending,
      uploadError: upload.error,
      upload: async (images: Parameters<typeof upload.mutateAsync>[0]) => {
        await upload.mutateAsync(images)
      },
      trackImageAdded: onImageAdded
    }
  }
}
